<template>
  <div class="reportconfig-container">
    <h5 class="header">
        {{ $t('comp.view.settingsCaps') }}
    </h5>

    <div class="settings-list">
    <div v-for="section of options" :key="section.key" class="config-entry">
      <div class="header config-row"
           tabindex="0"
           @keydown.space.prevent="toggleHeader(section.key);"
           @click="toggleHeader(section.key);">
        <span>{{ section.title }}</span>
        <i class="header-indicator fas fa-chevron-down checked" :class="{opened: headerState[section.key]}"></i>
      </div>

      <div class="body" v-bind:class="{'entry-expanded': headerState[section.key]}">
        <template v-for="(option, index) of section.options">

          <template v-if="option.type === 'select'">
            <div v-for="value of option.values"
                 :key="section.key + '.' + option.key + '.' + (value.key || value.value)"
                 class="content config-row"
                 :class="{'row-disabled': (section.disabled || option.disabled), 'row-deselected': configState[option.key] !== value.value}"
                 :tabindex="(headerState[section.key] && !(section.disabled || option.disabled)) ? 0 : -1"
                 @keydown.space.prevent="optionSelected(option.key, value.value, (section.disabled || option.disabled))"
                 @click="optionSelected(option.key, value.value, (section.disabled || option.disabled))">
              <span>{{ value.title }}</span>
              <i :class="[(configState[option.key] === value.value) ? 'fas fa-check-circle checked' : 'far fa-circle']"></i>
            </div>
          </template>

          <template v-else-if="option.type === 'multiple'">
            <div v-for="value of option.values"
                 :key="section.key + '.' + option.key + '.' + (value.key || value.value)"
                 class="content focusable"
                 :class="{'row-disabled': (section.disabled || option.disabled), 'row-deselected': multiSelectRowIsDeselected(option.key, value.key || value.value)}"
                 :tabindex="(headerState[section.key] && !(section.disabled || option.disabled)) ? 0 : -1"
                 @keydown.space.prevent="(section.disabled || option.disabled) ? null : multipleOptSelected(option.key, value.key || value.value)"
                 @click="(section.disabled || option.disabled) ? null : multipleOptSelected(option.key, value.key || value.value)">
              <div class="config-row">
                <span>{{ value.title }}</span>
                <i :class="multipleOptClasses(option.key, value.key || value.value)"></i>
              </div>
              <template v-if="Array.isArray(value.value)">
                <div v-for="subVal of value.value" v-bind:key="section.key + '.' + option.key + '.' + (value.key || value.value) + '.' + subVal.key"
                    class="config-row config-row-indented"
                    :class="{'row-disabled': (!section.disabled && option.disabled), 'row-deselected': multiSelectRowIsDeselected(option.key, value.key || value.value, subVal.value)}"
                    :tabindex="(headerState[section.key] && !(section.disabled || option.disabled)) ? 0 : -1"
                    @keydown.space.stop.prevent="(section.disabled || option.disabled) ? null : multipleOptSelected(option.key, value.key || value.value, subVal.value)"
                    @click.stop="(section.disabled || option.disabled) ? null : multipleOptSelected(option.key, value.key || value.value, subVal.value)">
                  <span>{{ subVal.title }}</span>
                  <i :class="multipleOptClasses(option.key, value.key || value.value, subVal.value)"></i>
                </div>
              </template>
            </div>
          </template>

          <template v-else-if="option.type === 'text'">
            <div :key="section.key + '.' + option.key" class="content">
              <input class="config-row config-textfield full"
                            v-model="configState[option.key]"
                            type="text"
                            :placeholder="option.placeholder"
                            :required="option.required"
                            :disabled="(section.disabled || option.disabled)"
                            @blur="optionSelected(option.key, configState[option.key], (section.disabled || option.disabled))"
                            @keydown.enter="optionSelected(option.key, configState[option.key], (section.disabled || option.disabled), $event)" />
            </div>
          </template>

          <hr class="option-separator" v-if="(index + 1) !== section.options.length" :key="'sep'+index"/>

        </template>
      </div>

    </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'ReportConfig',
  props: {
    options: Array,
  },
  watch: {
    options(val) {
      const { configState } = this.createState(val);
      this.configState = configState;
    },
  },
  data() {
    const { headerState, configState } = this.createState();
    return {
      headerState: { ...headerState },
      configState: { ...configState },
    };
  },
  computed: {},
  created() {
    this.configChanged();
  },
  methods: {
    createState(options) {
      if (!options) options = this.options;
      const headerState = Object.fromEntries(options.map(option => [ option.key, option.expanded ]));
      const configState = Object.fromEntries(
          options
              .reduce((acc, option) => acc.concat(option.options), [])
              .map(subOption => {
                  let value;
                  if (subOption.currentValue !== undefined) {
                      value = subOption.currentValue;
                  } else if (subOption.values) {
                      const defaultValue = subOption.values.find(val => val.default);
                      value = defaultValue ? defaultValue.value : (subOption.values[0] ? subOption.values[0].value : null);
                  } else {
                      value = null;
                  }
                  return [subOption.key, value];
              }),
      );
      return { headerState, configState };
    },
    toggleHeader(key) {
      this.headerState[key] = !this.headerState[key];
    },
    multiselectContainsSubValues(configKey, optionKey) {
      const configSection = this.options.flatMap(({ options }) => options).find((opt) => opt.key === configKey);
      if (!configSection) return false;
      return !!configSection.values.find(({ key, value }) => key === optionKey && Array.isArray(value));
    },
    getAvailableMultiSelectValues(configKey, optionKey) {
      const configSection = this.options.flatMap(({ options }) => options).find((opt) => opt.key === configKey);
      if (!configSection) return [];
      for (const val of configSection.values) {
        if (val.key === optionKey || val.value === optionKey)
          return Array.isArray(val.value) ? val.value.map(({ value }) => value) : [ val.value ];
      }
      return [];
    },
    multiSelectRowIsDeselected(configKey, optionKey, subOption) {
      const state = this.configState[configKey];
      return Array.isArray(state[optionKey])
          ? subOption === undefined
              ? state[optionKey].length === 0
              : state[optionKey].length !== 0 && !state[optionKey].includes(subOption) // return false (selected) if state[optionKey] (parent element) is already empty, to prevent opacity being reduced twice
          : !state[optionKey];
    },
    multipleOptClasses(configKey, optionKey, subOption) {
      const selectedClasses = [ 'fas fa-check-square', 'checked' ];
      const halfSelectedClasses = [ 'fas fa-minus-square', 'checked' ];
      const deselectedClasses = [ 'far fa-square', 'disabledColor' ];

      const optState = this.configState[configKey];
      if (!optState) return deselectedClasses;

      let selectedValues = optState[optionKey] ? optState[optionKey] : [];
      if (this.multiselectContainsSubValues(configKey, optionKey)) {
        if (!!subOption) {
          return selectedValues.includes(subOption) ? selectedClasses : deselectedClasses;
        } else if (selectedValues.length === 0)
          return deselectedClasses;
        else if (selectedValues.length < this.getAvailableMultiSelectValues(configKey, optionKey).length)
          return halfSelectedClasses;
        else
          return selectedClasses;
      }
      return !!optState[optionKey] ? selectedClasses : deselectedClasses;
    },
    multipleOptSelected(configKey, optionKey, subValue) {
      const hasSubOpts = this.multiselectContainsSubValues(configKey, optionKey)
        const currentOptState = {
            ...(this.configState[configKey] ? this.configState[configKey]
                : { [optionKey]: hasSubOpts ? [] : false })
        };
      if (hasSubOpts) { // the option state can contain sub values
        if (subValue === undefined) {
          const availableVals = this.getAvailableMultiSelectValues(configKey, optionKey);
          if (currentOptState[optionKey].length < availableVals.length)
              // not all sub values are checked, therefore mark them as checked (include all available values)
            currentOptState[optionKey] = availableVals;
          else
              // all sub values are checked, so mark them as unchecked
            currentOptState[optionKey] = []
        } else {
          const includesVal = currentOptState[optionKey].includes(subValue);
          if (includesVal)
              // if subVal is in array, remove it
            currentOptState[optionKey] = currentOptState[optionKey].filter((val) => val !== subValue);
          else
              // otherwise append it
            currentOptState[optionKey] = [ ...currentOptState[optionKey], subValue ];
        }
      } else { // the option is on
        currentOptState[optionKey] = !currentOptState[optionKey];
      }
      this.optionSelected(configKey, currentOptState);
    },
    optionSelected(key, value, disabled, $event) {
      if (disabled) return;
      if ($event && $event.target) ($event.target).blur();
      this.$set(this.configState, key, value);
      this.$emit('optionChanged', key, value);
      this.configChanged();
    },
    configChanged() {
      this.$emit('configChanged', this.configState);
    },
  },
}
</script>

<style scoped>
.reportconfig-container {
  text-align: left;
}

.config-entry {
  margin-top: 0.4rem;
  margin-bottom: 0.4rem;
  border-bottom: solid 1px var(--contrast-6);
}

.header {
  text-align: left;
  font-weight: bold;
  margin-bottom: 0.25rem;
}

.header > i.header-indicator {
  margin-right: 0.12rem;
  transform: rotate(0);
  transition: transform 125ms ease-in-out;
}

.header > i.header-indicator.opened {
  transform: rotate(180deg);
}

.config-entry > .body {
  max-height: 0;
  overflow-y: hidden;
  transition: max-height 250ms ease-in-out;
}

.config-entry > .body.entry-expanded {
  max-height: 100vh;
}

.config-entry > .body > .content {
  text-align: left;
  font-weight: normal;
  font-size: 0.9rem;
  padding: 2px 1px 2px 10px;
}

.config-row {
  display: flex;
  flex-direction: row;
  opacity: 1;
  cursor: pointer;
  color: var(--contrast-4);
  margin-bottom: 0.25rem;
  transition: opacity 75ms linear, text-shadow 75ms linear;
}

.config-row:hover:not(.header) {
  text-shadow: 0 1px 0 var(--contrast-3);
}

.config-row:focus, .focusable:focus {
  box-shadow: var(--contrast-1) 0 1px 0.5rem;
}

.row-disabled {
  opacity: .5;
  cursor: not-allowed;
}

.row-deselected {
  opacity: .65;
}

.config-row > * {
  cursor: pointer;
}

.row-disabled > *, .row-disabled > .config-row > * {
  cursor: not-allowed;
}

.config-row > span {
  flex: 1 1;
}

.config-row > i {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: var(--contrast-4);
  opacity: 0.9;
  transition: color 125ms linear;
}

.config-row-indented {
  padding-left: 0.5rem;
}

.config-row-indented > i {
  font-size: 0.75rem;
}

.config-textfield {
  border: 1px solid var(--contrast-3);
  background-color: var(--contrast-1), rgba(255, 255, 255, 0.5);
  color: var(--contrast-4);
  cursor: text;
  outline: none;
  box-shadow: none;
  border-radius: 0;
  transition: all 75ms linear;
  /*max-height: 0;*/
}

.config-textfield:hover {
  box-shadow: inset 0 -2px 2px -1px var(--contrast-2);
}

.config-textfield:focus {
  background-color: var(--contrast-1), rgba(255, 255, 255, 0.25);
  color: var(--contrast-4);
  box-shadow: inset 0 -2px 2px -1px var(--contrast-3);
  border: 1px solid var(--contrast-3);
}

.config-textfield::placeholder {
  color: var(--contrast-4);
  opacity: 0.5;
}

.entry-expanded .config-textfield {
  max-height: 100%;
}

.form-control {
  line-height: 2rem; /* set height to 2rem, otherwise ä, ö, ü characters will not be displayed correctly */
}

.option-separator {
  margin: 0.25rem 0 0.25rem 0.5rem;
  border-color: var(--contrast-6);
}

.wide-label {
    width: 400px;
}

.settings-list {
    overflow-y: auto;
    /* height: 23rem; */
}
</style>
